import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveComponent as _resolveComponent, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "student-paper-page" }
const _hoisted_2 = { class: "page-shell" }
const _hoisted_3 = { class: "hero-section" }
const _hoisted_4 = { class: "hero-stat" }
const _hoisted_5 = { class: "list-title" }
const _hoisted_6 = {
  key: 0,
  class: "paper-grid"
}
const _hoisted_7 = { class: "paper-card-head" }
const _hoisted_8 = { class: "paper-title" }
const _hoisted_9 = { class: "paper-sub" }
const _hoisted_10 = { class: "paper-meta-row" }
const _hoisted_11 = { class: "paper-footer" }
const _hoisted_12 = { class: "pager-wrap" }

import { computed, onMounted, reactive, ref, watch } from "vue";
import { message } from "ant-design-vue";
import {
  getStudentTestPaper,
  getTestPaperQuestion,
  getTestPaperQuestionScore as getTestPaperQuestionScoreApi,
} from "@/servers/api/tp";
import {
  getStudentWrongQuestionRecommend as getStudentWrongQuestionRecommendApi,
  getStudentWrongQuestionSearch as searchStudentWrongQuestionApi,
} from "@/servers/api/wrongQuestion";
import { useUserStore } from "@/store";
import router from "@/router";

type PaperItem = {
  id: number;
  exam_id: number;
  exam_name?: string;
  exam_student_grade?: string;
  name: string;
  subject?: string;
  file_path?: string;
  pdf_file_path?: string;
  word_file_path?: string;
  doc_file_path?: string;
  document_file_path?: string;
  create_time?: string;
  [key: string]: any;
};

type QuestionItem = {
  id?: number;
  string_number?: string;
  correct_rate?: number;
  keywords?: string[] | string;
  answer?: string;
  score?: number | string;
  images?: string[] | string;
  [key: string]: any;
};

type QuestionScoreRecord = {
  student_id?: string;
  score_detail?: any;
  [key: string]: any;
};

type WrongQuestionSearchRecord = {
  class_id?: string;
  question_key?: string;
  question_id?: number;
  string_number?: string;
  question_type?: string;
  knowledge_points?: string[];
  student_score?: number;
  full_score?: number;
  wrong_rate?: number;
  wrong_count?: number;
  participant_count?: number;
  [key: string]: any;
};

type ScoreMap = Record<string, number | string>;


export default /*@__PURE__*/_defineComponent({
  __name: 'StudentPaperView',
  setup(__props) {

const userStore = useUserStore();
const userInfo = ref<any>(null);
const loadingPapers = ref(false);
const loadingQuestions = ref(false);
const loadingQuestionScores = ref(false);
const paperList = ref<PaperItem[]>([]);
const allPaperOptions = ref<PaperItem[]>([]);
const questionList = ref<QuestionItem[]>([]);
const selectedPaper = ref<PaperItem | null>(null);
const studentQuestionScoreMap = ref<ScoreMap>({});
const examOptions = ref<{ label: string; value: number }[]>([]);
const showOnlyWrong = ref(false);
const loadingWrongQuestionSearch = ref(false);
const wrongQuestionSearchList = ref<WrongQuestionSearchRecord[]>([]);
const wrongQuestionSearchPagination = reactive({
  page: 1,
  size: 8,
  total: 0,
});
const wrongQuestionSearchFilters = reactive<{
  question_type?: string;
  knowledge_keyword?: string;
}>({
  question_type: undefined,
  knowledge_keyword: "",
});
const wrongQuestionTypeOptions = ref<{ label: string; value: string }[]>([]);
const wrongQuestionRecommendKnowledge = ref<{ name: string; count: number }[]>([]);

const filters = reactive<{
  student_grade?: string;
  subject?: string;
  name?: string;
}>({
  student_grade: undefined,
  subject: undefined,
  name: undefined,
});

const pagination = reactive({
  page: 1,
  size: 9,
  total: 0,
});
const getTestPaperFileUrl = (path: string) => `/api/tp/file?path=${encodeURIComponent(path || "")}`;
const getPaperFilePath = (paper: PaperItem) =>
  String(
    paper.pdf_file_path ||
      paper.word_file_path ||
      paper.doc_file_path ||
      paper.document_file_path ||
      paper.file_path ||
      ""
  ).trim();
const getPaperDownloadName = (paper: PaperItem, path: string) => {
  const rawName = (path.split(/[\\/]/).pop() || "").split("?")[0].trim();
  let fileName = rawName;
  try {
    fileName = decodeURIComponent(rawName);
  } catch (_error) {
    fileName = rawName;
  }
  return fileName || paper.name || `试卷${paper.id}`;
};
const getStudentTestPaperApi = (student_uid: string, params: Record<string, any>) =>
  getStudentTestPaper({ student_uid, ...params });
const getTestPaperQuestionsApi = (test_paper_id: number) =>
  getTestPaperQuestion({ test_paper_id });

const subjectOptions = [
  { label: "语文", value: "语文" },
  { label: "数学", value: "数学" },
  { label: "英语", value: "英语" },
  { label: "物理", value: "物理" },
  { label: "化学", value: "化学" },
  { label: "生物", value: "生物" },
  { label: "政治", value: "政治" },
  { label: "历史", value: "历史" },
  { label: "地理", value: "地理" },
];

const getPaperGrade = (paper: PaperItem) =>
  String(paper.exam_student_grade || paper.student_grade || "").trim();

const sortLabelOptions = <T extends { label: string }>(options: T[]) =>
  options.sort((a, b) => b.label.localeCompare(a.label, "zh-Hans-CN", { numeric: true }));

const gradeOptions = computed(() => {
  const gradeMap = new Map<string, string>();
  for (const paper of allPaperOptions.value) {
    const grade = getPaperGrade(paper);
    if (grade) gradeMap.set(grade, grade);
  }
  return sortLabelOptions(Array.from(gradeMap.values()).map((grade) => ({ label: grade, value: grade })));
});

const availableSubjectOptions = computed(() => {
  const subjectMap = new Map<string, string>();
  for (const paper of allPaperOptions.value) {
    if (filters.student_grade && getPaperGrade(paper) !== filters.student_grade) continue;
    const subject = String(paper.subject || "").trim();
    if (subject) subjectMap.set(subject, subject);
  }
  const dynamicOptions = Array.from(subjectMap.values()).map((subject) => ({
    label: subject,
    value: subject,
  }));
  return dynamicOptions.length ? sortLabelOptions(dynamicOptions) : subjectOptions;
});

const paperNameOptions = computed(() => {
  const nameMap = new Map<string, string>();
  for (const paper of allPaperOptions.value) {
    if (filters.student_grade && getPaperGrade(paper) !== filters.student_grade) continue;
    if (filters.subject && paper.subject !== filters.subject) continue;
    const name = String(paper.name || "").trim();
    if (name) nameMap.set(name, name);
  }
  return sortLabelOptions(Array.from(nameMap.values()).map((name) => ({ label: name, value: name })));
});

const wrongQuestionSearchColumns = [
  { title: "题号", dataIndex: "string_number", width: 90 },
  { title: "题型", dataIndex: "question_type", width: 130 },
  { title: "知识点", dataIndex: "knowledge_points", ellipsis: true },
  { title: "我的得分", dataIndex: "student_score", width: 100 },
  { title: "题目满分", dataIndex: "full_score", width: 100 },
  { title: "班级错误率", dataIndex: "wrong_rate", width: 120 },
  { title: "班级错题人数", dataIndex: "wrong_count", key: "wrong_count", width: 120 },
];

const studentUid = computed(() => {
  const uid = String(userInfo.value?.student?.uid || "").trim();
  if (uid) return uid;
  return String(userInfo.value?.role || "").trim();
});

const normalizeStringArray = (value: any): string[] => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item || "").trim()).filter(Boolean);
  }
  if (typeof value === "string") {
    const text = value.trim();
    if (!text) return [];
    try {
      const parsed = JSON.parse(text);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item || "").trim()).filter(Boolean);
      }
    } catch (_error) {
      return [text];
    }
    return [text];
  }
  return [];
};

const getQuestionContent = (question: QuestionItem) => {
  const keys = ["question", "question_text", "content", "stem", "title", "text", "description"];
  for (const key of keys) {
    const value = question?.[key];
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text) return text;
  }
  return "";
};

const normalizeQuestionKey = (value: any) => {
  const text = String(value ?? "").trim();
  if (!text) return "";
  const match = text.match(/\d+/);
  if (match) return String(Number(match[0]));
  return text;
};

const parseScoreValue = (value: any): number | string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = String(value).trim();
  if (!text || text.toLowerCase() === "nan") return null;
  const parsed = Number(text);
  if (!Number.isNaN(parsed)) return parsed;
  return text;
};

const normalizeScoreDetail = (scoreDetail: any): ScoreMap => {
  let detail = scoreDetail;
  if (typeof detail === "string") {
    const text = detail.trim();
    if (!text) return {};
    try {
      detail = JSON.parse(text);
    } catch (_error) {
      return {};
    }
  }

  const scoreMap: ScoreMap = {};
  const mergeEntry = (obj: Record<string, any>) => {
    for (const [key, value] of Object.entries(obj || {})) {
      const questionKey = normalizeQuestionKey(key);
      if (!questionKey) continue;
      const parsedValue = parseScoreValue(value);
      if (parsedValue === null) continue;
      scoreMap[questionKey] = parsedValue;
    }
  };

  if (Array.isArray(detail)) {
    for (const item of detail) {
      if (item && typeof item === "object" && !Array.isArray(item)) {
        mergeEntry(item as Record<string, any>);
      }
    }
    return scoreMap;
  }

  if (detail && typeof detail === "object") {
    mergeEntry(detail as Record<string, any>);
  }
  return scoreMap;
};

const getQuestionDisplayKey = (question: QuestionItem, index: number) => {
  return normalizeQuestionKey(question.string_number || index + 1);
};

const parseNumberValue = (value: any): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const text = String(value ?? "").trim();
  if (!text) return null;
  const matched = text.match(/-?\d+(?:\.\d+)?/);
  if (!matched) return null;
  const parsed = Number(matched[0]);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
};

const formatScore = (value: number | string | null | undefined) => {
  if (value === undefined || value === null || value === "") return "-";
  if (typeof value === "number") {
    if (Number.isInteger(value)) return String(value);
    return String(Number(value.toFixed(2)));
  }
  return String(value);
};

const formatPercent = (value: any) => {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return "-";
  return `${(numberValue * 100).toFixed(1)}%`;
};

const formatKnowledgePoints = (value: any) => {
  const list = normalizeStringArray(value);
  if (!list.length) return "-";
  return list.join("、");
};

const getQuestionOwnScore = (question: QuestionItem, index: number) => {
  const key = getQuestionDisplayKey(question, index);
  if (!key) return undefined;
  return studentQuestionScoreMap.value[key];
};

const getQuestionTotalScore = (question: QuestionItem) => {
  return parseScoreValue(question.score);
};

const isWrongQuestion = (question: QuestionItem, index: number) => {
  const ownScore = parseNumberValue(getQuestionOwnScore(question, index));
  const totalScore = parseNumberValue(getQuestionTotalScore(question));
  if (totalScore === null) return false;
  if (ownScore === null) return true;
  return ownScore < totalScore - 1e-6;
};

const wrongQuestionCount = computed(() => {
  return questionList.value.reduce((count, question, index) => {
    return isWrongQuestion(question, index) ? count + 1 : count;
  }, 0);
});

const displayQuestionList = computed(() => {
  if (!showOnlyWrong.value) return questionList.value;
  return questionList.value.filter((question, index) => isWrongQuestion(question, index));
});

const escapeHtml = (text: string) => {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
};

const renderMarkdown = (rawValue: string) => {
  const escaped = escapeHtml(String(rawValue || ""));
  return escaped
    .replace(/^######\s+(.+)$/gm, "<h6>$1</h6>")
    .replace(/^#####\s+(.+)$/gm, "<h5>$1</h5>")
    .replace(/^####\s+(.+)$/gm, "<h4>$1</h4>")
    .replace(/^###\s+(.+)$/gm, "<h3>$1</h3>")
    .replace(/^##\s+(.+)$/gm, "<h2>$1</h2>")
    .replace(/^#\s+(.+)$/gm, "<h1>$1</h1>")
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.+?)\*/g, "<em>$1</em>")
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    )
    .replace(/\n/g, "<br/>");
};

const buildQueryParams = () => {
  const params: Record<string, any> = {
    page: pagination.page,
    size: pagination.size,
  };
  if (filters.student_grade) params.student_grade = filters.student_grade;
  if (filters.subject) params.subject = filters.subject;
  if (filters.name) params.name = filters.name;
  return params;
};

const loadExamOptions = async () => {
  if (!studentUid.value) return;
  const response = await getStudentTestPaperApi(studentUid.value, { page: 1, size: 1000 });
  if (response.code !== 200) return;

  allPaperOptions.value = (response.data || []).map((item: any) => ({
    ...item,
    exam_name: item.exam_name || `考试${item.exam_id}`,
  }));

  const examMap = new Map<number, string>();
  for (const paper of allPaperOptions.value) {
    const examId = Number(paper.exam_id);
    if (!Number.isFinite(examId)) continue;
    examMap.set(examId, paper.exam_name || `考试${examId}`);
  }
  examOptions.value = Array.from(examMap.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => b.value - a.value);
};

const loadQuestionScores = async (paper: PaperItem) => {
  studentQuestionScoreMap.value = {};
  if (!paper?.id || !paper?.exam_id || !studentUid.value) return;

  loadingQuestionScores.value = true;
  try {
    const response = await getTestPaperQuestionScoreApi({
      exam_id: Number(paper.exam_id),
      test_paper_id: Number(paper.id),
      student_id: studentUid.value,
    });
    if (response.code !== 200) {
      message.error("小题得分加载失败");
      return;
    }
    const records = Array.isArray(response.data) ? (response.data as QuestionScoreRecord[]) : [];
    if (records.length) {
      studentQuestionScoreMap.value = normalizeScoreDetail(records[0].score_detail);
    }
  } catch (_error) {
    message.error("小题得分加载失败");
  } finally {
    loadingQuestionScores.value = false;
  }
};

const loadQuestions = async (paperId: number) => {
  loadingQuestions.value = true;
  try {
    const response = await getTestPaperQuestionsApi(paperId);
    if (response.code !== 200) {
      message.error("题目加载失败");
      questionList.value = [];
      return;
    }
    questionList.value = Array.isArray(response.data) ? response.data : [];
  } catch (_error) {
    message.error("题目加载失败");
    questionList.value = [];
  } finally {
    loadingQuestions.value = false;
  }
};

const loadWrongQuestionRecommend = async () => {
  wrongQuestionRecommendKnowledge.value = [];
  wrongQuestionTypeOptions.value = [];
  if (!studentUid.value || !selectedPaper.value?.id || !selectedPaper.value?.exam_id) return;

  try {
    const response = await getStudentWrongQuestionRecommendApi({
      student_uid: studentUid.value,
      exam_id: Number(selectedPaper.value.exam_id),
      test_paper_id: Number(selectedPaper.value.id),
      question_type: String(wrongQuestionSearchFilters.question_type || "").trim() || undefined,
      knowledge_keyword:
        String(wrongQuestionSearchFilters.knowledge_keyword || "").trim() || undefined,
      limit: 12,
    });
    if (response.code !== 200) return;
    const data = response.data || {};
    const knowledgeList = Array.isArray(data.knowledge_points) ? data.knowledge_points : [];
    const typeList = Array.isArray(data.question_types) ? data.question_types : [];
    wrongQuestionRecommendKnowledge.value = knowledgeList;
    wrongQuestionTypeOptions.value = typeList
      .map((item: any) => {
        const name = String(item?.name || "").trim();
        if (!name) return null;
        return { label: name, value: name };
      })
      .filter(Boolean) as { label: string; value: string }[];
  } catch (_error) {
    // ignore recommend failures
  }
};

const loadWrongQuestionSearch = async (resetPage = false) => {
  if (resetPage) {
    wrongQuestionSearchPagination.page = 1;
  }
  wrongQuestionSearchList.value = [];
  if (!studentUid.value || !selectedPaper.value?.id || !selectedPaper.value?.exam_id) return;

  loadingWrongQuestionSearch.value = true;
  try {
    const response = await searchStudentWrongQuestionApi({
      student_uid: studentUid.value,
      page: wrongQuestionSearchPagination.page,
      size: wrongQuestionSearchPagination.size,
      exam_id: Number(selectedPaper.value.exam_id),
      test_paper_id: Number(selectedPaper.value.id),
      question_type: String(wrongQuestionSearchFilters.question_type || "").trim() || undefined,
      knowledge_keyword:
        String(wrongQuestionSearchFilters.knowledge_keyword || "").trim() || undefined,
    });
    if (response.code !== 200) {
      message.error("错题搜索失败");
      return;
    }
    wrongQuestionSearchList.value = Array.isArray(response.data) ? response.data : [];
    wrongQuestionSearchPagination.total = Number(response.total || 0);

    const merged = new Map<string, { label: string; value: string }>();
    for (const option of wrongQuestionTypeOptions.value) {
      merged.set(option.value, option);
    }
    for (const item of wrongQuestionSearchList.value) {
      const typeValue = String(item.question_type || "").trim();
      if (typeValue && !merged.has(typeValue)) {
        merged.set(typeValue, { label: typeValue, value: typeValue });
      }
    }
    wrongQuestionTypeOptions.value = Array.from(merged.values());
  } catch (_error) {
    message.error("错题搜索失败");
  } finally {
    loadingWrongQuestionSearch.value = false;
  }
};

const triggerWrongQuestionSearch = async () => {
  await loadWrongQuestionSearch(true);
  await loadWrongQuestionRecommend();
};

const useRecommendKnowledge = async (name: string) => {
  wrongQuestionSearchFilters.knowledge_keyword = name;
  await triggerWrongQuestionSearch();
};

const resetWrongQuestionSearchFilters = async () => {
  wrongQuestionSearchFilters.question_type = undefined;
  wrongQuestionSearchFilters.knowledge_keyword = "";
  wrongQuestionSearchPagination.page = 1;
  await triggerWrongQuestionSearch();
};

const handleWrongQuestionSearchPageChange = async (page: number) => {
  wrongQuestionSearchPagination.page = page;
  await loadWrongQuestionSearch(false);
};

const handleWrongQuestionSearchSizeChange = async (_current: number, size: number) => {
  wrongQuestionSearchPagination.size = size;
  wrongQuestionSearchPagination.page = 1;
  await loadWrongQuestionSearch(false);
};

const loadPaperDetail = async (paper: PaperItem) => {
  await Promise.all([loadQuestions(Number(paper.id)), loadQuestionScores(paper)]);
};

const selectPaper = async (paper: PaperItem) => {
  selectedPaper.value = paper;
  showOnlyWrong.value = false;
  await loadPaperDetail(paper);
};

const loadPapers = async (resetPage = false) => {
  if (!studentUid.value) return;
  if (resetPage) pagination.page = 1;

  loadingPapers.value = true;
  try {
    const response = await getStudentTestPaperApi(studentUid.value, buildQueryParams());
    if (response.code !== 200) {
      message.error(response.msg || "试卷列表加载失败");
      return;
    }

    paperList.value = (response.data || []).map((item: any) => ({
      ...item,
      exam_name: item.exam_name || `考试${item.exam_id}`,
    }));
    pagination.total = Number(response.total || 0);

    if (!paperList.value.length) {
      selectedPaper.value = null;
      questionList.value = [];
      studentQuestionScoreMap.value = {};
      wrongQuestionSearchList.value = [];
      wrongQuestionRecommendKnowledge.value = [];
      wrongQuestionTypeOptions.value = [];
      wrongQuestionSearchPagination.total = 0;
      return;
    }

    selectedPaper.value = null;
    questionList.value = [];
    studentQuestionScoreMap.value = {};
    wrongQuestionSearchList.value = [];
    wrongQuestionRecommendKnowledge.value = [];
    wrongQuestionTypeOptions.value = [];
    wrongQuestionSearchPagination.total = 0;
  } catch (_error) {
    message.error("试卷列表加载失败");
  } finally {
    loadingPapers.value = false;
  }
};

const downloadPaperFile = (paper: PaperItem) => {
  const filePath = getPaperFilePath(paper);
  if (!filePath) {
    message.warning("该试卷暂无可下载文件");
    return;
  }
  const link = document.createElement("a");
  link.href = getTestPaperFileUrl(filePath);
  link.download = getPaperDownloadName(paper, filePath);
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const handlePageChange = async (page: number) => {
  pagination.page = page;
  await loadPapers(false);
};

const resetFilters = async () => {
  filters.student_grade = undefined;
  filters.subject = undefined;
  filters.name = undefined;
  pagination.page = 1;
  await loadPapers(false);
};

watch(
  () => filters.student_grade,
  () => {
    filters.subject = undefined;
    filters.name = undefined;
  }
);

watch(
  () => filters.subject,
  () => {
    filters.name = undefined;
  }
);

onMounted(async () => {
  userInfo.value = userStore.getUserInfo();
  if (!userInfo.value || !userInfo.value.role) {
    message.error("请先登录");
    return;
  }

  if (userInfo.value.teacher) {
    message.info("老师账号请使用老师试卷查看页面");
    router.replace("/paper_view");
    return;
  }

  if (!studentUid.value) {
    message.error("未识别到学生身份");
    return;
  }

  await loadExamOptions();
  await loadPapers(true);
});

return (_ctx: any,_cache: any) => {
  const _component_a_select = _resolveComponent("a-select")!
  const _component_a_button = _resolveComponent("a-button")!
  const _component_a_space = _resolveComponent("a-space")!
  const _component_a_card = _resolveComponent("a-card")!
  const _component_a_tag = _resolveComponent("a-tag")!
  const _component_a_empty = _resolveComponent("a-empty")!
  const _component_a_spin = _resolveComponent("a-spin")!
  const _component_a_pagination = _resolveComponent("a-pagination")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("section", _hoisted_2, [
      _createElementVNode("header", _hoisted_3, [
        _cache[5] || (_cache[5] = _createElementVNode("div", null, [
          _createElementVNode("h2", null, "我的试卷"),
          _createElementVNode("p", null, "按考试、科目或名称查找试卷，点击查看后下载文件。")
        ], -1)),
        _createElementVNode("div", _hoisted_4, [
          _createElementVNode("span", null, _toDisplayString(pagination.total), 1),
          _cache[4] || (_cache[4] = _createElementVNode("small", null, "份可查看试卷", -1))
        ])
      ]),
      _createVNode(_component_a_card, {
        class: "filter-card",
        bordered: false
      }, {
        default: _withCtx(() => [
          _createVNode(_component_a_space, {
            wrap: "",
            class: "filter-actions"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_a_select, {
                value: filters.student_grade,
                "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((filters.student_grade) = $event)),
                options: gradeOptions.value,
                "allow-clear": "",
                placeholder: "按年级筛选",
                class: "filter-control"
              }, null, 8, ["value", "options"]),
              _createVNode(_component_a_select, {
                value: filters.subject,
                "onUpdate:value": _cache[1] || (_cache[1] = ($event: any) => ((filters.subject) = $event)),
                options: availableSubjectOptions.value,
                "allow-clear": "",
                placeholder: "按科目筛选",
                class: "filter-control filter-control-sm"
              }, null, 8, ["value", "options"]),
              _createVNode(_component_a_select, {
                value: filters.name,
                "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((filters.name) = $event)),
                options: paperNameOptions.value,
                "allow-clear": "",
                "show-search": "",
                "option-filter-prop": "label",
                placeholder: "按试卷筛选",
                class: "filter-input"
              }, null, 8, ["value", "options"]),
              _createVNode(_component_a_button, {
                type: "primary",
                onClick: _cache[3] || (_cache[3] = ($event: any) => (loadPapers(true)))
              }, {
                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                  _createTextVNode("查询", -1)
                ]))]),
                _: 1
              }),
              _createVNode(_component_a_button, { onClick: resetFilters }, {
                default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                  _createTextVNode("重置", -1)
                ]))]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_a_card, {
        class: "paper-list-card",
        bordered: false
      }, {
        title: _withCtx(() => [
          _createElementVNode("div", _hoisted_5, [
            _cache[8] || (_cache[8] = _createElementVNode("span", null, "试卷列表", -1)),
            _createElementVNode("small", null, "共 " + _toDisplayString(pagination.total) + " 份", 1)
          ])
        ]),
        default: _withCtx(() => [
          _createVNode(_component_a_spin, { spinning: loadingPapers.value }, {
            default: _withCtx(() => [
              (paperList.value.length)
                ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(paperList.value, (paper) => {
                      return (_openBlock(), _createElementBlock("article", {
                        key: paper.id,
                        class: "paper-card"
                      }, [
                        _createElementVNode("div", _hoisted_7, [
                          _createElementVNode("div", null, [
                            _createElementVNode("div", _hoisted_8, _toDisplayString(paper.name || `试卷${paper.id}`), 1),
                            _createElementVNode("div", _hoisted_9, _toDisplayString(paper.exam_name || `考试${paper.exam_id}`), 1)
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_10, [
                          _createVNode(_component_a_tag, { color: "blue" }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(paper.subject || "-"), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createVNode(_component_a_tag, { color: "green" }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(paper.exam_student_grade || "-"), 1)
                            ]),
                            _: 2
                          }, 1024),
                          _createElementVNode("span", null, "试卷 ID：" + _toDisplayString(paper.id), 1)
                        ]),
                        _createElementVNode("div", _hoisted_11, [
                          _createElementVNode("span", null, _toDisplayString(paper.create_time || "-"), 1),
                          _createVNode(_component_a_button, {
                            type: "primary",
                            size: "small",
                            onClick: ($event: any) => (downloadPaperFile(paper))
                          }, {
                            default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                              _createTextVNode("查看/下载", -1)
                            ]))]),
                            _: 1
                          }, 8, ["onClick"])
                        ])
                      ]))
                    }), 128))
                  ]))
                : (_openBlock(), _createBlock(_component_a_empty, {
                    key: 1,
                    description: "暂无可查看试卷"
                  }))
            ]),
            _: 1
          }, 8, ["spinning"]),
          _createElementVNode("div", _hoisted_12, [
            _createVNode(_component_a_pagination, {
              current: pagination.page,
              "page-size": pagination.size,
              total: pagination.total,
              "show-size-changer": false,
              "show-total": (total) => `共 ${total} 份`,
              onChange: handlePageChange
            }, null, 8, ["current", "page-size", "total", "show-total"])
          ])
        ]),
        _: 1
      })
    ])
  ]))
}
}

})